import { useEffect } from 'react';

import { useOnboardingState } from 'entities/onboarding/api/useOnboardingState';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { useWindowWidth } from 'shared/lib/screen/useScreenSize';
import { useSidebarStore } from 'shared/lib/sidebar/useSidebarStore';

const OPEN_CLASS = 'assistant-panel-open';
// Above this there is room for the navigation and the panel to be expanded at once. Below it the two
// together leave too little page between them, so the navigation drops to its rail while the panel
// is open and comes back when it closes.
const NAV_AND_PANEL_FIT_FROM = 1920;

/**
 * Reserve page width for the docked assistant panel while it is open.
 *
 * The class goes on the document element rather than a wrapper because the page banners are
 * `position: fixed` siblings of the content column, so both have to read the same custom property.
 * Whether the reservation applies at all is a media query's call in `app/styles/style.css`.
 */
export function useAssistantPanelReservation({ enabled }: { enabled: boolean }): void {
  const isOpen = useOnboardingDialogStore((state) => state.isOpen);
  const { data: onboardingState } = useOnboardingState({ enabled });
  const setSidebarSuppressed = useSidebarStore((state) => state.setSuppressed);
  const width = useWindowWidth();
  // Released the moment the panel starts leaving, so the page widens on the same beat.
  const isReserved = enabled && isOpen && onboardingState?.layout === 'docked';
  const crowdsNavigation = isReserved && width < NAV_AND_PANEL_FIT_FROM;

  useEffect(() => {
    if (!isReserved) {
      return;
    }
    document.documentElement.classList.add(OPEN_CLASS);
    return () => document.documentElement.classList.remove(OPEN_CLASS);
  }, [isReserved]);

  useEffect(() => {
    setSidebarSuppressed(crowdsNavigation);
    return () => setSidebarSuppressed(false);
  }, [crowdsNavigation, setSidebarSuppressed]);
}
